import { type ReactNode, useEffect } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useCrisisStore } from '../../lib/stores/crisisStore'
import { LoadingScreen } from './LoadingScreen'

interface CrisisGuardProps {
  children: ReactNode
}

export function CrisisGuard({ children }: CrisisGuardProps) {
  const location = useLocation()
  const { activeCrisis, isLoading, initState } = useCrisisStore()

  console.debug('[CrisisGuard] Render:', {
    pathname: location.pathname,
    activeCrisis: activeCrisis ? { id: activeCrisis.id, status: activeCrisis.status } : null,
    isLoading,
    initState,
  })

  // IMPORTANT: All hooks must be called before any conditional returns (Rules of Hooks)
  // Note: 2FA setup is handled entirely within LoginPage - users requiring 2FA setup
  // will have isAuthenticated=false and won't reach CrisisGuard

  // Redirect to crisis select if ready but no active crisis
  useEffect(() => {
    const shouldRedirect =
      initState === 'ready' &&
      !activeCrisis &&
      location.pathname !== '/crisis/select' &&
      !location.pathname.startsWith('/profile') &&
      !location.pathname.startsWith('/admin')

    if (shouldRedirect) {
      console.debug('[CrisisGuard] No active crisis, redirecting to /crisis/select')
      window.location.href = '/crisis/select'
    }
  }, [initState, activeCrisis, location.pathname])

  // If we're on the crisis select page, always allow access (for creating/selecting crisis)
  if (location.pathname === '/crisis/select') {
    console.debug('[CrisisGuard] On /crisis/select, allowing access')
    return <>{children}</>
  }

  // Allow access to profile and admin pages even without active crisis
  if (location.pathname.startsWith('/profile') || location.pathname.startsWith('/admin')) {
    console.debug('[CrisisGuard] On profile/admin page, allowing access without active crisis')
    return <>{children}</>
  }

  // Show loading state while initializing or loading
  if (initState === 'idle' || initState === 'initializing' || isLoading) {
    console.debug(
      '[CrisisGuard] Showing loading state (initState:',
      initState,
      ', isLoading:',
      isLoading,
      ')'
    )
    return <LoadingScreen showCard={false} />
  }

  // If still waiting for redirect, show nothing
  if (initState === 'ready' && !activeCrisis) {
    return null
  }

  // Error state - allow access but user will see empty state or can navigate manually
  if (initState === 'error') {
    console.debug('[CrisisGuard] Error state, allowing access')
    return <>{children}</>
  }

  // If active crisis exists, show the normal app
  console.debug('[CrisisGuard] Active crisis exists, showing children')
  return <>{children}</>
}
